/**
 * AI Import wizard step machine.
 *
 * `nextAIImportStep` derives the wizard step from a loaded job's status given
 * the step the user is currently on. It only moves the user FORWARD (or to the
 * processing view, which doubles as the failed surface) — it never yanks them
 * backward. In particular, a job sitting in 'completed_pending_review' must not
 * pull a user off the 'decision' step (the deal/no-deal choice) back to
 * 'review' when the job query refetches mid-apply.
 */
import type { AIImportStatus } from '@/types/ai-import';

export type AIImportStep = 'upload' | 'processing' | 'review' | 'decision' | 'confirm';

export function nextAIImportStep(current: AIImportStep, status: AIImportStatus): AIImportStep {
  switch (status) {
    case 'pending':
    case 'running':
      // Still analyzing. Stay put if the user has already advanced past it.
      return current === 'review' || current === 'decision' || current === 'confirm'
        ? current
        : 'processing';
    case 'completed_pending_review':
      // Analyzed. Land on review unless the user has already advanced to the
      // decision/confirm steps (status stays 'completed_pending_review' until
      // Apply finishes, so this must not bounce them backward).
      return current === 'decision' || current === 'confirm' ? current : 'review';
    case 'applied':
      return 'confirm';
    case 'failed':
      // The processing step renders the failure surface.
      return 'processing';
    case 'cancelled':
    default:
      return current;
  }
}
